import { useEffect, useState } from 'react';

import { TagDTO } from '@/types/api-types';

import IconClose from '@/assets/svg/icon-close.svg?react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';

import { useGlobalStore } from '@/stores/globalStore';

import { apiService } from '@/services/api-service';

export function AddTagsTab() {
  const [isOpen, setIsOpen] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [deleteTagUuid, setDeleteTagUuid] = useState<string>('');
  const { allTags, setAllTags } = useGlobalStore();

  const [selectedTags, setSelectedTags] = useState<TagDTO[]>(
    Object.values(allTags.entities),
  );

  useEffect(() => {
    setSelectedTags(Object.values(allTags.entities));
  }, [allTags.entities]);

  const handleAddTag = async () => {
    if (newTag.trim()) {
      try {
        const newTagResponse = await apiService.createTag({
          name: newTag,
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          agreements: [],
        });

        setAllTags({
          entities: {
            ...allTags.entities,
            [newTagResponse.uuid]: newTagResponse,
          },
          rootIds: [...allTags.rootIds, newTagResponse.uuid],
          totalCount: allTags.totalCount + 1,
        });

        setIsOpen(false);
        setNewTag('');
      } catch (error) {}
    }
  };

  const handleRemoveTag = async (uuid: string) => {
    try {
      await apiService.deleteTag(uuid);
      const newEntities = { ...allTags.entities };
      delete newEntities[uuid];

      setAllTags({
        entities: newEntities,
        rootIds: allTags.rootIds.filter((id) => id !== uuid),
        totalCount: allTags.totalCount - 1,
      });
    } catch (error) {}

    setDeleteTagUuid('');
  };

  return (
    <>
      <ConfirmationDialog
        isOpen={!!deleteTagUuid}
        onClose={() => setDeleteTagUuid('')}
        onConfirm={() => handleRemoveTag(deleteTagUuid)}
        variant="default"
        title="Ви впевнені, що бажаєте видалити мітку?"
        confirmText="Видалити"
      />
      <Card className="w-full shadow-sm sm:w-[490px]">
        <CardHeader className="pb-[40px]">
          <CardTitle className="text-2xl font-semibold text-primary">
            Мітки
          </CardTitle>
          <p className="text-base font-medium text-primary">
            Блок видалення та створення міток для файлів
          </p>
        </CardHeader>

        <CardContent>
          <div className="relative min-h-[270px] rounded-md border border-gray-200 p-4">
            <span className="absolute -top-2.5 bg-white px-1 text-sm text-gray-200">
              Мітки файлів
            </span>

            <div className="flex flex-wrap gap-2">
              {selectedTags.map((tag) => (
                <Badge
                  key={tag.uuid}
                  variant="outline"
                  className="h-6 gap-1 rounded border-separator bg-white px-1 py-1 text-sm font-normal text-primary"
                >
                  {tag.name}
                  <button
                    className="relative z-10 rounded-sm hover:bg-blue-500 hover:[&>svg]:fill-white"
                    onClick={() => handleRemoveTag(tag.uuid)}
                  >
                    <IconClose className="z-0 min-h-3 min-w-3 fill-blue-500 p-0.5 transition-colors" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button onClick={() => setIsOpen(true)}>Додати</Button>
          </div>

          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="pb-7 sm:max-w-[380px]">
              <DialogHeader>
                <DialogTitle className="mb-3 text-2xl font-semibold">
                  Мітки
                </DialogTitle>
              </DialogHeader>
              <div className="flex justify-between gap-4">
                <Input
                  placeholder="Нова мітка"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                />
                <Button variant="outline" onClick={handleAddTag}>
                  Додати
                </Button>
              </div>
            </DialogContent>
            <DialogDescription className="text-base text-primary" hidden>
              {newTag}
            </DialogDescription>
          </Dialog>
        </CardContent>
      </Card>
    </>
  );
}
